'use client';

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import type { ProductFile } from '@eliadmin/core/src/api/products/types/product-types';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useProductPriceDisplay } from '@eliadmin/core/src/components/nt/product/product-price';
import { IconButton } from '@eliadmin/core/src/components/nt/ui/icon-button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { imageSource, resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';

import { useActiveVariant } from './use-active-variant';

const LightboxComponent = lazy(() =>
  import('@eliadmin/core/src/components/lightbox/lightbox-component').then(module => ({
    default: module.LightboxComponent,
  })),
);

/** Figma „a.chip“ on the photo (same as the product card): Body/XS, 8 × 6 px, radius 8. */
const CHIP = 'nt-body-xs rounded-[8px] px-2 py-1.5';
const TAG_TEXT = { light: '#FFFFFF', dark: '#0D110D' };
/*
 * Offsets inside the 1 px border: chips and arrows sit 24 px (16 px below sm) from the outer frame
 * edge; the photo box is 520² of the 650 × 618 inner box at 1728 (10 % / 7.93 % insets).
 */
const ARROW = 'absolute top-1/2 z-[1] -translate-y-1/2';

/** Main photo, its gallery and the photos of every variant, each URL once (core `ProductImages`). */
function galleryImages(
  product: { image?: ProductFile | null; images?: ProductFile[] | null },
  variants: { images?: ProductFile[] | null }[],
) {
  const seen = new Set<string>();
  return [
    product.image,
    ...(product.images ?? []),
    ...variants.flatMap(v => v.images ?? []),
  ].filter((image): image is ProductFile => {
    const url = imageSource(image);
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

/**
 * Figma „Gallery“ (112:1289): white 652 × 620 frame with the photo (520² contain), the discount and
 * tag chips, prev/next Outline M buttons and a scrolling row of 105² thumbnails. Choosing a variant
 * with its own photos jumps to them (only them with the store setting „gallery with image
 * variants“); the photo opens the core lightbox at the current image.
 */
export function ProductGallery() {
  const { t } = useTranslations();
  const product = useVariantsStore(state => state.product);
  const onlyVariantImages = useGlobalStore(
    state => state.templateSettings.product_detail?.gallery_with_image_variants?.enabled,
  );
  const variant = useActiveVariant();
  const price = useProductPriceDisplay(variant ?? product);

  const variantImages = useMemo(() => galleryImages({ images: variant?.images }, []), [variant]);
  const images = useMemo(
    () =>
      onlyVariantImages && variantImages.length
        ? variantImages
        : galleryImages(product, product.variants ?? []),
    [onlyVariantImages, variantImages, product],
  );

  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const trackRef = useRef<HTMLUListElement>(null);
  const current = images[Math.min(active, images.length - 1)];

  // A variant with its own photos shows its first one; other variants keep the current photo.
  useEffect(() => {
    const first = imageSource(variantImages[0]);
    const index = first ? images.findIndex(image => imageSource(image) === first) : -1;
    if (index >= 0) setActive(index);
  }, [variantImages, images]);

  // Keep the selected thumbnail in view without scrolling the page.
  useEffect(() => {
    const track = trackRef.current;
    const thumb = track?.children[active];
    if (!track || !(thumb instanceof HTMLElement)) return;
    const start = thumb.offsetLeft - track.offsetLeft;
    const end = start + thumb.offsetWidth;
    if (start < track.scrollLeft) track.scrollTo({ left: start });
    else if (end > track.scrollLeft + track.clientWidth)
      track.scrollTo({ left: end - track.clientWidth });
  }, [active]);

  const go = (direction: 1 | -1) =>
    setActive(index => (index + direction + images.length) % images.length);

  return (
    <div className="flex min-w-0 flex-col gap-3 sm:gap-4">
      <div className="relative aspect-[652/620] overflow-hidden rounded-2xl border border-[#E2E5DF] bg-white">
        {current ? (
          <button
            type="button"
            onClick={() => setLightbox(true)}
            aria-label={resolveImageAlt(current, product.name)}
            className="absolute inset-x-[10%] inset-y-[7.93%] cursor-zoom-in rounded-[8px] focus-visible:shadow-focus focus-visible:outline-none"
          >
            <Image
              key={imageSource(current)}
              src={imageSource(current)!}
              alt=""
              fill
              preload={active === 0}
              sizes="(min-width: 1280px) 520px, (min-width: 1024px) 40vw, 80vw"
              className="object-contain"
            />
          </button>
        ) : null}

        <div className="absolute left-[15px] top-[15px] flex max-w-[calc(100%-30px)] flex-wrap gap-1 sm:left-[23px] sm:top-[23px] sm:max-w-[calc(100%-46px)]">
          {price.showPromotion ? (
            <span className={cn(CHIP, 'bg-[#D92D20] text-white')}>
              {`${t('text3265')} -${price.promotionDiscountLabel}%`}
            </span>
          ) : null}
          {product.tags?.map(tag => (
            <span
              key={tag.id ?? tag.name}
              className={cn(CHIP, !tag.color && 'bg-[#00A3E0] text-white')}
              style={
                tag.color
                  ? { backgroundColor: tag.color, color: getContrastYIQ(tag.color, TAG_TEXT) }
                  : undefined
              }
            >
              {tag.name}
            </span>
          ))}
        </div>

        {images.length > 1 ? (
          <>
            <IconButton
              icon="caret-left"
              label={t('text3525')}
              onClick={() => go(-1)}
              className={cn(ARROW, 'left-[15px] sm:left-[23px]')}
            />
            <IconButton
              icon="caret-right"
              label={t('text3526')}
              onClick={() => go(1)}
              className={cn(ARROW, 'right-[15px] sm:right-[23px]')}
            />
          </>
        ) : null}
      </div>

      {images.length > 1 ? (
        <ul
          ref={trackRef}
          role="list"
          aria-label={t('text2246')}
          className="hide-scroll-bar flex snap-x gap-3 overflow-x-auto motion-safe:scroll-smooth sm:gap-4"
        >
          {images.map((image, index) => (
            <li key={imageSource(image)} className="shrink-0 snap-start">
              <button
                type="button"
                aria-label={resolveImageAlt(image, product.name)}
                aria-current={index === active ? 'true' : undefined}
                onClick={() => setActive(index)}
                className={cn(
                  'relative block size-[72px] overflow-hidden rounded-xl bg-white transition-opacity sm:size-[105px]',
                  'focus-visible:shadow-focus focus-visible:outline-none',
                  // Selected: 2 px ink ring drawn over the photo.
                  index === active
                    ? 'after:absolute after:inset-0 after:rounded-xl after:ring-2 after:ring-inset after:ring-[#0D110D]'
                    : 'hover:opacity-80',
                )}
              >
                <Image
                  src={imageSource(image)!}
                  alt=""
                  fill
                  sizes="(min-width: 640px) 105px, 72px"
                  className="object-contain"
                />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {lightbox ? (
        <Suspense fallback={null}>
          <LightboxComponent
            isOpen={lightbox}
            setIsOpen={setLightbox}
            // The core lightbox opens at its first slide: start the list at the current photo.
            images={[...images.slice(active), ...images.slice(0, active)]}
            zoom={{ maxZoomPixelRatio: 8 }}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
